import { EmptyState, EmptyStateDescription, EmptyStateHeader } from "@relex-solutions/fragments"
import { lazy, Suspense, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { type RouteObject, useParams, useSearchParams } from "react-router"
import { CustomerLayout } from "@/domains/customers/components/CustomerLayout/CustomerLayout"
import { useGetFormMenuItems } from "@/domains/customers/hooks/useGetFormMenuItems"
import { CustomerHierarchyPage } from "@/domains/customers/pages/CustomerHierarchyPage"
import { CustomerListPage } from "@/domains/customers/pages/CustomerListPage"
import { CustomerPage } from "@/domains/customers/pages/CustomerPage"
import { MicrofrontendPage } from "@/domains/customers/pages/MicroFrontendPage"
import { ErrorComponent } from "@/shared/components/ErrorComponent/ErrorComponent"
import { CUSTOMERS_BASE_PATH } from "@/shared/constants/routePaths"
import { Loading } from "../Loading/Loading"
import styles from "./CustomerRoutes.module.css"
import { RouterErrorBoundary } from "./RouterErrorBoundary/RouterErrorBoundary"

const DynamicFormPage = lazy(() =>
  import("@/domains/customers/pages/DynamicFormPage/DynamicFormPage").then((m) => ({
    default: m.DynamicFormPage
  }))
)

function DynamicFormPageWrapper() {
  const { t } = useTranslation()
  const { formId, customerId } = useParams<{ formId: string; customerId: string }>()
  const [searchParams] = useSearchParams()
  const multiDeployment = searchParams.get("multiDeployment") === "true" // Temporary flag to enable multi-deployment mode

  if (!formId || !customerId) {
    throw new Error("Missing required route parameters")
  }

  const { data: menuItems, isLoading, isError } = useGetFormMenuItems(true, customerId)

  const isFormVisible = useMemo(() => {
    if (!menuItems || !formId) return false
    // NOTE: Form needs to be explicitly marked as hidden in the menu_titles endpoint to be hidden from the user
    // This is deliberate (fail-open for UX) and will be removed when we have a proper way to hide forms from the user.
    return !menuItems.some((item) => item.upsertId === formId && item.isVisible === false)
  }, [menuItems, formId])

  if (isLoading) {
    return <Loading text={t("dynamicForm.loading")} />
  }

  if (isError) {
    return <ErrorComponent error={new Error(t("dynamicForm.menuTitlesLoadError"))} hideIconAndTitle />
  }

  if (!isFormVisible) {
    return (
      <EmptyState className={styles.emptyState}>
        <EmptyStateHeader>{t("dynamicForm.noViewAccessTitle")}</EmptyStateHeader>
        <EmptyStateDescription>{t("dynamicForm.noViewAccess")}</EmptyStateDescription>
      </EmptyState>
    )
  }

  return (
    <Suspense fallback={<Loading text={t("dynamicForm.loading")} />}>
      <DynamicFormPage formId={formId} customerId={customerId} multiDeployment={multiDeployment} />
    </Suspense>
  )
}

export const CustomerRoutes: RouteObject = {
  path: CUSTOMERS_BASE_PATH.slice(1),
  errorElement: <RouterErrorBoundary />,
  children: [
    {
      index: true,
      element: <CustomerListPage />,
      errorElement: <RouterErrorBoundary />
    },
    {
      path: ":customerId",
      element: <CustomerLayout />,
      errorElement: <RouterErrorBoundary />,
      children: [
        {
          path: "overview",
          element: <CustomerPage />,
          errorElement: <RouterErrorBoundary />
        },
        {
          path: "plan-resources",
          element: <CustomerHierarchyPage />,
          errorElement: <RouterErrorBoundary />
        },
        {
          path: "form/:formId",
          element: <DynamicFormPageWrapper />,
          errorElement: <RouterErrorBoundary />
        },
        {
          path: ":microFrontendId",
          element: <MicrofrontendPage />,
          errorElement: <RouterErrorBoundary />
        }
      ]
    }
  ]
}
